/**
 * Monster Onesie — video tiles (BRIEF §4.5, §5.7).
 *
 * ONE implementation, shared by the PDP video row and the homepage UGC row
 * via snippets/monster-video-tile.liquid. Do not fork a second copy.
 *
 * Behaviour, identical in both places: the poster shows with a centred play
 * button, the first tap loads and plays the clip inline — muted, playsinline,
 * looping, never autoplaying — and subsequent taps toggle play/pause. There
 * are no native controls and no lightbox.
 *
 * Sound is opt-in per play. A small toggle appears on the playing tile only;
 * pausing re-mutes, so every play starts silent no matter what the last one
 * did. Autoplay policy is unaffected — play() is still called on a muted
 * element, and the unmute only ever happens on a real tap.
 *
 * ONE tile plays at a time, page-wide. Starting a tile stops every other
 * playing tile in the DOCUMENT, not just in this row — the PDP carries one
 * row today and the homepage another, and the rule has to survive the day
 * they share a page. That makes the search deliberately wider than `this`.
 *
 * preload="none" means nothing downloads until a tap, which matters on a
 * homepage carrying four of these. The <video> is only given its source at
 * that moment, so an untouched row costs nothing.
 */

class MonsterVideoRow extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * Sound is opt-in per play: the clip loads muted, and pausing re-mutes it so
   * the next play starts silent again. Nothing here ever unmutes on its own.
   *
   * @param {HTMLElement} tile
   * @param {HTMLVideoElement} video
   */
  #setMuted(tile, video, muted) {
    video.muted = muted;

    const button = tile.querySelector('[data-video-sound]');
    if (!button) return;

    button.setAttribute('aria-pressed', String(!muted));
    button.setAttribute('aria-label', muted ? 'Turn sound on' : 'Turn sound off');
  }

  /**
   * The single pause path. Both callers — the tap-to-pause branch and the
   * stop-the-others sweep — go through here, so a tile stopped by another
   * tile's tap is in exactly the same state as one the visitor paused by
   * hand: poster and play button back, silent, and its sound button reading
   * "Turn sound on" again. Two copies of this would drift the moment either
   * side gained a step.
   *
   * @param {HTMLElement} tile
   * @param {HTMLVideoElement} video
   */
  #stop(tile, video) {
    video.pause();
    tile.removeAttribute('data-playing');
    this.#setMuted(tile, video, true);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tile = event.target.closest?.('[data-video-tile]');
    if (!tile) return;

    const video = tile.querySelector('video');
    if (!video) return;

    // The sound button sits inside the tile, so its clicks arrive here too.
    // Toggling sound must never toggle playback — hence the early return
    // rather than a stopPropagation on the button, which would put the
    // knowledge of this component's behaviour in two places.
    if (event.target.closest?.('[data-video-sound]')) {
      this.#setMuted(tile, video, !video.muted);
      return;
    }

    // First tap: attach the source. Until now the tile is just a poster.
    if (!video.src && video.dataset.src) {
      video.src = video.dataset.src;
    }

    if (video.paused) {
      // Flip the state FIRST, then undo it if play is actually refused.
      //
      // Waiting on the play() promise looked correct and is not: the promise
      // can stay pending indefinitely — a backgrounded tab suspends media and
      // never settles it — while the video plays regardless. That left the
      // poster and play button covering a playing clip. Optimistic is right
      // here because the failure path is recoverable and the success path is
      // the common one.
      // Silence is asserted at the START of every play, not just on pause.
      //
      // Re-muting only in the pause branch below looks equivalent and is not:
      // that branch is keyed on video.paused, so any cycle that ends without a
      // real pause — a play() that never started, a clip the browser suspended
      // — leaves muted=false behind, and the NEXT tap would open with sound.
      // Asserting it here makes "starts silent" true regardless of how the
      // previous cycle ended, which is the actual promise being made.
      this.#setMuted(tile, video, true);

      // One tile at a time, page-wide. Queried off `document` rather than
      // `this` on purpose: two rows sharing a page must still honour the
      // rule, and each row is its own component instance that would
      // otherwise only ever police its own tiles.
      //
      // The self-skip is load-bearing, not defensive noise. A tile can sit
      // at data-playing="true" while video.paused is still true — the flag
      // is set optimistically and a suspended clip never resolves play() —
      // so the tapped tile can match this selector on its own tap. Without
      // the guard it would stop itself and then immediately play, arriving
      // muted-but-correct today and mis-stating its sound button tomorrow.
      for (const other of document.querySelectorAll('[data-video-tile][data-playing]')) {
        if (other === tile) continue;

        const otherVideo = other.querySelector('video');
        if (otherVideo) this.#stop(other, otherVideo);
      }

      tile.setAttribute('data-playing', 'true');
      video.play().catch(() => tile.removeAttribute('data-playing'));
    } else {
      // Belt and braces with the assert above: keeps the button's own state
      // honest while the tile sits paused.
      this.#stop(tile, video);
    }
  };
}

if (!customElements.get('monster-video-row')) {
  customElements.define('monster-video-row', MonsterVideoRow);
}
